import { useMemo } from 'react';
import { validateCompanyCode } from '@app/lib/company-code/validateCompanyCode';
import { parseLithuanianPersonalCode } from '@app/lib/personal-code/parseLithuanianPersonalCode';
import { FormStatus } from '@app/lib/form/contracts/FormStatus';
import { ViolationMap } from '@app/lib/form/contracts/ViolationMap';
import { PurchaseTravelInsuranceResult } from '@app/service/eshop/travel/actions/purchaseTravelInsurance';
import { InsuredTraveler } from '@app/domain/model/eshop/travel/InsuredTraveler';

interface FormState {
    status: FormStatus;
    data?: PurchaseTravelInsuranceResult;
    violations?: ViolationMap;
    message?: string;
}

interface UseComputedValuesParams {
    formState: FormState;
    insuredPersons: InsuredTraveler[];
    clientValidationErrors: Nullable<ViolationMap>;
}

export function useTravelWizardComputedValues({
    formState,
    insuredPersons,
    clientValidationErrors,
}: UseComputedValuesParams) {
    const isCompany = useMemo(() => {
        const personalCode = formState.data?.formData?.personalCode || '';
        if (!personalCode) return false;

        return validateCompanyCode(personalCode).isValid;
    }, [formState.data?.formData?.personalCode]);

    const shouldSkipInsuredPersonsStep = useMemo(() => {
        const policyholderIsTraveler = formState.data?.formData?.policyholderIsTraveler || false;

        if (!policyholderIsTraveler) {
            return false;
        }

        if (insuredPersons.length > 1) {
            return false;
        }

        const hasAgeMismatch = insuredPersons.some(person => {
            if (!person.personalCode) {
                return false;
            }
            const parsedData = parseLithuanianPersonalCode(person.personalCode);
            return parsedData.isValid && parsedData.age !== undefined && parsedData.age !== person.age;
        });

        return !hasAgeMismatch;
    }, [formState.data?.formData?.policyholderIsTraveler, insuredPersons]);

    const mergedFormState = useMemo(() => {
        if (!clientValidationErrors) {
            return formState;
        }

        return {
            ...formState,
            violations: {
                ...formState.violations,
                ...clientValidationErrors,
            },
        };
    }, [formState, clientValidationErrors]);

    return {
        isCompany,
        shouldSkipInsuredPersonsStep,
        mergedFormState,
    };
}
